/**
 * Admin → Training → Edit module (Task #18).
 *
 * Three sections, top-to-bottom:
 *  1. Module form (title, slug, target roles, delivery, passing score,
 *     estimated minutes, sort order, prerequisites, required, active).
 *  2. Lessons — list with ↑/↓/edit/delete + inline lesson editor that
 *     uses the block builder (Quill for paragraphs).
 *  3. Quiz questions — list with add/edit/delete; option editor enforces
 *     exactly-one-correct (server backs this up via Zod refinement).
 *
 * Everything is autosave-free: each save is an explicit user action so
 * partial drafts don't leak into the catalog.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LessonBlock,
  ModuleAdminDetail,
  ModuleAdminSummary,
  QuizQuestionAdmin,
  TrainingLesson,
  TrainingModuleUpdate,
} from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';
import { Checkbox, Input, Textarea } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';
import { LessonBlockEditor } from '../components/training/lesson-block-editor.js';
import { ScormPackagesPanel } from '../components/training/scorm-package-list.js';
import {
  QuestionEditor,
  draftToPayload,
  emptyQuestionDraft,
  type QuestionDraft,
} from '../components/training/question-editor.js';

interface ModuleForm {
  title: string;
  slug: string;
  description: string;
  target_roles: string; // comma-separated, parsed on save
  delivery_format: 'self_study' | 'remote' | 'in_person' | 'hybrid';
  audience: 'internal' | 'external';
  passing_score: number;
  estimated_minutes: number;
  sort_order: number;
  prerequisite_ids: string[];
  is_required: boolean;
  is_active: boolean;
  // Task #21 — certificate expiry & renewal reminders.
  expires_after_days: number | null;
  reminder1_days_before: number | null;
  reminder2_days_before: number | null;
  reminder_template_id: string | null;
}

interface MessageTemplateLite {
  id: string;
  name: string;
  channel: 'voice' | 'sms' | 'email';
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    const d = (err.body as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
    return JSON.stringify(d);
  }
  return (err as Error).message;
}

export function AdminTrainingEditPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['admin', 'training', 'module', id],
    queryFn: () => api.get<ModuleAdminDetail>(`/v1/admin/training/modules/${id}`),
    enabled: !!id,
  });
  const allModulesQ = useQuery({
    queryKey: ['admin', 'training', 'modules'],
    queryFn: () => api.get<{ modules: ModuleAdminSummary[] }>('/v1/admin/training/modules'),
  });

  const [form, setForm] = useState<ModuleForm | null>(null);
  const [moduleErr, setModuleErr] = useState<string | null>(null);

  useEffect(() => {
    const d = detailQ.data;
    if (!d) return;
    setForm({
      title: d.module.title,
      slug: d.module.slug,
      description: d.module.description ?? '',
      target_roles: (d.module.target_roles ?? []).join(', '),
      delivery_format: d.module.delivery_format,
      audience: d.module.audience ?? 'internal',
      passing_score: d.module.passing_score,
      estimated_minutes: d.module.estimated_minutes,
      sort_order: d.module.sort_order,
      prerequisite_ids: d.module.prerequisite_ids ?? [],
      is_required: d.module.is_required,
      is_active: d.module.is_active,
      expires_after_days: d.module.expires_after_days ?? null,
      reminder1_days_before: d.module.reminder1_days_before ?? null,
      reminder2_days_before: d.module.reminder2_days_before ?? null,
      reminder_template_id: d.module.reminder_template_id ?? null,
    });
  }, [detailQ.data]);

  // Task #21 — email templates list for the "renewal reminder" picker.
  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: () =>
      api.get('/v1/message-templates') as Promise<{ items: MessageTemplateLite[] }>,
  });
  const emailTemplates = (templatesQ.data?.items ?? []).filter(
    (t) => t.channel === 'email',
  );

  const saveModule = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const payload: TrainingModuleUpdate = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        target_roles: form.target_roles
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        delivery_format: form.delivery_format,
        audience: form.audience,
        passing_score: form.passing_score,
        estimated_minutes: form.estimated_minutes,
        sort_order: form.sort_order,
        prerequisite_ids: form.prerequisite_ids,
        is_required: form.is_required,
        is_active: form.is_active,
        expires_after_days: form.expires_after_days,
        reminder1_days_before: form.reminder1_days_before,
        reminder2_days_before: form.reminder2_days_before,
        reminder_template_id: form.reminder_template_id,
      };
      await api.patch(`/v1/admin/training/modules/${id}`, payload);
    },
    onSuccess: () => {
      setModuleErr(null);
      qc.invalidateQueries({ queryKey: ['admin', 'training'] });
    },
    onError: (err) => setModuleErr(extractDetail(err)),
  });

  const deleteModule = useMutation({
    mutationFn: () => api.delete(`/v1/admin/training/modules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'training'] });
      navigate('/admin/training');
    },
  });

  if (detailQ.isLoading || !form) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (detailQ.isError || !detailQ.data) {
    return (
      <p className="text-sm text-danger">
        Failed to load. <Link to="/admin/training" className="underline">Back</Link>
      </p>
    );
  }

  const detail = detailQ.data;
  const otherModules = (allModulesQ.data?.modules ?? []).filter((m) => m.id !== id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Link to="/admin/training" className="text-sm text-ink-muted hover:text-ink">← All modules</Link>
          <h1 className="text-2xl font-semibold mt-1">{form.title || '(untitled)'}</h1>
          <p className="text-xs text-ink-muted">
            {detail.lessons.length} lessons · {detail.questions.length} questions
            {!form.is_active && <> · <Badge tone="warning">Inactive</Badge></>}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Module</CardTitle></CardHeader>
        <CardBody className="space-y-3">
          {moduleErr && <FormBanner message={moduleErr} />}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Title" required>
              {(id) => (
                <Input id={id} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              )}
            </FormField>
            <FormField label="Slug" required>
              {(id) => (
                <Input id={id} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
              )}
            </FormField>
          </div>
          <FormField label="Description">
            {(id) => (
              <Textarea id={id} rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            )}
          </FormField>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormField label="Delivery format">
              {(id) => (
                <select id={id}
                  className="block w-full rounded border border-border bg-surface px-3 py-2 text-ink"
                  value={form.delivery_format}
                  onChange={(e) => setForm({ ...form, delivery_format: e.target.value as ModuleForm['delivery_format'] })}>
                  <option value="self_study">Self study</option>
                  <option value="remote">Remote</option>
                  <option value="in_person">In person</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              )}
            </FormField>
            <FormField label="Audience" hint="Internal = staff users. External = customer-facing (enrollment + magic links).">
              {(id) => (
                <select id={id}
                  className="block w-full rounded border border-border bg-surface px-3 py-2 text-ink"
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value as 'internal' | 'external' })}>
                  <option value="internal">Internal (staff)</option>
                  <option value="external">External (customer-facing)</option>
                </select>
              )}
            </FormField>
            <FormField label="Passing score (%)">
              {(id) => (
                <Input id={id} type="number" min={0} max={100} value={form.passing_score}
                  onChange={(e) => setForm({ ...form, passing_score: Number(e.target.value) || 0 })} />
              )}
            </FormField>
            <FormField label="Estimated minutes">
              {(id) => (
                <Input id={id} type="number" min={0} value={form.estimated_minutes}
                  onChange={(e) => setForm({ ...form, estimated_minutes: Number(e.target.value) || 0 })} />
              )}
            </FormField>
            <FormField label="Sort order">
              {(id) => (
                <Input id={id} type="number" value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })} />
              )}
            </FormField>
          </div>
          <FormField label="Target roles" hint="Comma-separated list. Use 'all' for everyone.">
            {(id) => (
              <Input id={id} value={form.target_roles}
                onChange={(e) => setForm({ ...form, target_roles: e.target.value })} />
            )}
          </FormField>
          <FormField label="Prerequisite modules" hint="Learners must complete these before this module unlocks.">
            {() => (
              <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded p-2">
                {otherModules.length === 0 && (
                  <p className="text-xs text-ink-muted">No other modules in this tenant yet.</p>
                )}
                {otherModules.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.prerequisite_ids.includes(m.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          prerequisite_ids: e.target.checked
                            ? [...form.prerequisite_ids, m.id]
                            : form.prerequisite_ids.filter((x) => x !== m.id),
                        })
                      }
                    />
                    <span>{m.title}</span>
                    <span className="text-xs text-ink-muted">{m.slug}</span>
                  </label>
                ))}
              </div>
            )}
          </FormField>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_required}
                onChange={(e) => setForm({ ...form, is_required: e.target.checked })} />
              Required for certificate
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active (visible to learners)
            </label>
          </div>
          <div className="flex justify-between pt-2">
            <Button type="button" variant="secondary"
              onClick={() => {
                if (confirm('Deactivate this module? Learners will no longer see it.')) {
                  deleteModule.mutate();
                }
              }}
            >
              Deactivate module
            </Button>
            <Button onClick={() => saveModule.mutate()} disabled={saveModule.isPending}>
              {saveModule.isPending ? 'Saving…' : 'Save module'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Certification renewal</CardTitle></CardHeader>
        <CardBody className="space-y-3">
          <p className="text-xs text-ink-muted">
            When set, issued certificates for this module expire after the
            given number of days. Learners receive up to two renewal-reminder
            emails before the expiry date. Leave any field blank to disable
            that step.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Expires after (days)" hint="Blank = certificate never expires.">
              {(fid) => (
                <Input
                  id={fid}
                  type="number"
                  min={1}
                  max={3650}
                  value={form.expires_after_days ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expires_after_days:
                        e.target.value === '' ? null : Number(e.target.value) || null,
                    })
                  }
                />
              )}
            </FormField>
            <FormField label="Reminder template" hint="Email template used for renewal reminders. Falls back to a sensible default.">
              {(fid) => (
                <select
                  id={fid}
                  className="block w-full rounded border border-border bg-surface px-3 py-2 text-ink"
                  value={form.reminder_template_id ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reminder_template_id: e.target.value || null,
                    })
                  }
                >
                  <option value="">(default body)</option>
                  {emailTemplates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
            </FormField>
            <FormField label="First reminder — days before expiry" hint="e.g. 30">
              {(fid) => (
                <Input
                  id={fid}
                  type="number"
                  min={0}
                  max={365}
                  value={form.reminder1_days_before ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reminder1_days_before:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              )}
            </FormField>
            <FormField label="Second reminder — days before expiry" hint="e.g. 7">
              {(fid) => (
                <Input
                  id={fid}
                  type="number"
                  min={0}
                  max={365}
                  value={form.reminder2_days_before ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reminder2_days_before:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              )}
            </FormField>
          </div>
        </CardBody>
      </Card>

      <LessonsSection moduleId={id} lessons={detail.lessons} />
      <QuestionsSection moduleId={id} questions={detail.questions} />
      {form.audience === 'external' && (
        <EnrollmentsSection moduleId={id} />
      )}
    </div>
  );
}

// ─── Task #19 — Customer-facing enrollments (external modules only) ─────

interface EnrollmentRow {
  id: string;
  contact_profile_id: string;
  contact_name: string | null;
  organization_name: string | null;
  reply_to_email: string | null;
  enrolled_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_accessed_at: string | null;
  done_lessons: number;
  total_lessons: number;
  best_quiz_score: number | null;
  certificate_ref: string | null;
}

interface ContactSummary {
  id: string;
  contact_name: string | null;
  name: string;
  organization_name: string | null;
  reply_to_email: string | null;
}

function EnrollmentsSection({ moduleId }: { moduleId: string }): JSX.Element {
  const qc = useQueryClient();
  const enrollQ = useQuery({
    queryKey: ['admin', 'training', 'enrollments', moduleId],
    queryFn: () =>
      api.get<{ enrollments: EnrollmentRow[] }>(
        `/v1/admin/training/modules/${moduleId}/enrollments`,
      ),
  });
  const contactsQ = useQuery({
    queryKey: ['contact-profiles', 'all'],
    queryFn: () => api.get<{ profiles: ContactSummary[] }>('/v1/contact-profiles?limit=500'),
  });

  const [picked, setPicked] = useState<string[]>([]);
  const [ttlDays, setTtlDays] = useState(90);
  const [sendEmail, setSendEmail] = useState(true);
  const [created, setCreated] = useState<
    Array<{ contact_profile_id: string; magic_url: string | null; skipped: boolean }>
  >([]);
  const [err, setErr] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api.post<{
        results: Array<{ contact_profile_id: string; magic_url: string | null; skipped: boolean }>;
      }>(`/v1/admin/training/modules/${moduleId}/enrollments`, {
        contact_profile_ids: picked,
        expires_in_days: ttlDays,
        send_email: sendEmail,
      }),
    onSuccess: (r) => {
      setCreated(r.results);
      setPicked([]);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin', 'training', 'enrollments', moduleId] });
    },
    onError: (e) => setErr(extractDetail(e)),
  });

  const revokeMut = useMutation({
    mutationFn: (enrollmentId: string) =>
      api.delete(`/v1/admin/training/modules/${moduleId}/enrollments/${enrollmentId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'training', 'enrollments', moduleId] }),
  });

  const rows = enrollQ.data?.enrollments ?? [];
  const contacts = contactsQ.data?.profiles ?? [];

  return (
    <Card>
      <CardHeader><CardTitle>Customer-facing enrollments</CardTitle></CardHeader>
      <CardBody className="space-y-4">
        {err && <FormBanner message={err} />}

        <div className="border border-border rounded p-3 space-y-2">
          <div className="text-sm font-medium">Enroll new contacts</div>
          <div className="max-h-48 overflow-y-auto border border-border rounded p-2 space-y-1">
            {contacts.length === 0 && (
              <p className="text-xs text-ink-muted">No contact profiles in this tenant.</p>
            )}
            {contacts.map((c) => {
              const label = c.contact_name ?? c.name;
              return (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={picked.includes(c.id)}
                    onChange={(e) =>
                      setPicked((p) =>
                        e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id),
                      )
                    }
                  />
                  <span className="flex-1">{label}</span>
                  <span className="text-xs text-ink-muted">{c.reply_to_email ?? 'no email'}</span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm flex items-center gap-2">
              Expires in
              <Input
                type="number"
                min={1}
                max={365}
                className="w-20"
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.target.value) || 90)}
              />
              days
            </label>
            <label className="text-sm flex items-center gap-2">
              <Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              Email the learner now
            </label>
            <div className="flex-1" />
            <Button
              disabled={picked.length === 0 || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Enrolling…' : `Enroll ${picked.length} contact(s)`}
            </Button>
          </div>
          {created.length > 0 && (
            <div className="text-xs space-y-1 pt-2 border-t border-border">
              <div className="font-medium">Magic links (copy if email delivery fails):</div>
              {created.map((c) => (
                <div key={c.contact_profile_id} className="break-all">
                  {c.skipped ? (
                    <span className="text-ink-muted">Already enrolled · {c.contact_profile_id}</span>
                  ) : (
                    <code className="font-mono">{c.magic_url ?? '(no url)'}</code>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 pr-3">Learner</th>
                <th className="py-2 pr-3">Progress</th>
                <th className="py-2 pr-3">Quiz best</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Last access</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-ink-muted">
                    No enrollments yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="py-2 pr-3">
                    <div className="font-medium">{r.contact_name ?? '(unnamed)'}</div>
                    <div className="text-xs text-ink-muted">{r.organization_name ?? ''}</div>
                    <div className="text-xs text-ink-muted">{r.reply_to_email ?? ''}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {r.done_lessons} / {r.total_lessons} lessons
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {r.best_quiz_score === null ? '—' : `${Math.round(r.best_quiz_score)}%`}
                  </td>
                  <td className="py-2 pr-3">
                    {r.revoked_at ? (
                      <Badge tone="neutral">Revoked</Badge>
                    ) : r.certificate_ref ? (
                      <Badge tone="success">Certified</Badge>
                    ) : new Date(r.expires_at).getTime() < Date.now() ? (
                      <Badge tone="warning">Expired</Badge>
                    ) : (
                      <Badge tone="neutral">Active</Badge>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-ink-muted">
                    {r.last_accessed_at
                      ? new Date(r.last_accessed_at).toLocaleDateString()
                      : 'never'}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {!r.revoked_at && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          if (confirm('Revoke this enrollment? The magic link will stop working.')) {
                            revokeMut.mutate(r.id);
                          }
                        }}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Lessons ──────────────────────────────────────────────────────────────

function LessonsSection({
  moduleId,
  lessons,
}: {
  moduleId: string;
  lessons: TrainingLesson[];
}): JSX.Element {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'training', 'module', moduleId] });

  const createLesson = useMutation({
    mutationFn: () =>
      api.post(`/v1/admin/training/modules/${moduleId}/lessons`, {
        title: 'New lesson',
        content: [],
        lesson_type: 'reading',
        estimated_minutes: 5,
      }),
    onSuccess: (resp) => {
      setCreating(false);
      invalidate();
      const id = (resp as { id?: string })?.id;
      if (id) setEditingId(id);
    },
    onError: (e) => setErr(extractDetail(e)),
  });
  const deleteLesson = useMutation({
    mutationFn: (lessonId: string) =>
      api.delete(`/v1/admin/training/modules/${moduleId}/lessons/${lessonId}`),
    onSuccess: () => invalidate(),
    onError: (e) => setErr(extractDetail(e)),
  });
  const reorder = useMutation({
    mutationFn: (lesson_ids: string[]) =>
      api.post(`/v1/admin/training/modules/${moduleId}/lessons/reorder`, { lesson_ids }),
    onSuccess: () => invalidate(),
    onError: (e) => setErr(extractDetail(e)),
  });

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const dropReorder = (from: number, to: number) => {
    if (from === to) return;
    const ids = lessons.map((l) => l.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved!);
    reorder.mutate(ids);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Lessons ({lessons.length})</CardTitle>
          <Button onClick={() => { setCreating(true); createLesson.mutate(); }}
            disabled={creating || createLesson.isPending}>
            + New lesson
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {err && <FormBanner message={err} />}
        {lessons.length === 0 && (
          <p className="text-sm text-ink-muted">No lessons yet. Add the first one above.</p>
        )}
        {lessons.length > 1 && (
          <p className="text-xs text-ink-muted">Tip: drag the ⋮⋮ handle to reorder lessons.</p>
        )}
        {lessons.map((lesson, idx) => (
          <div
            key={lesson.id}
            className={
              'border rounded transition-colors ' +
              (overIdx === idx && dragIdx !== null && dragIdx !== idx
                ? 'border-accent ring-2 ring-accent/40'
                : 'border-border')
            }
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
            onDragLeave={() => setOverIdx((cur) => (cur === idx ? null : cur))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIdx !== null) dropReorder(dragIdx, idx);
              setDragIdx(null); setOverIdx(null);
            }}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-surface-alt">
              <div className="flex items-center gap-2">
                <span
                  draggable
                  onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className="cursor-grab active:cursor-grabbing select-none text-ink-muted px-1"
                  title="Drag to reorder"
                  aria-label={`Drag handle for lesson ${idx + 1}`}
                  role="button"
                >⋮⋮</span>
                <span className="text-xs text-ink-muted w-6">#{idx + 1}</span>
                <span className="font-medium">{lesson.title}</span>
                <Badge tone="neutral">{lesson.lesson_type}</Badge>
                <span className="text-xs text-ink-muted">{lesson.estimated_minutes}m</span>
              </div>
              <div className="flex gap-1">
                <Button type="button" variant="secondary"
                  onClick={() => setEditingId(editingId === lesson.id ? null : lesson.id)}>
                  {editingId === lesson.id ? 'Close' : 'Edit'}
                </Button>
                <button type="button" className="text-xs px-2 py-1 rounded border border-border text-danger"
                  onClick={() => {
                    if (confirm(`Delete lesson "${lesson.title}"?`)) deleteLesson.mutate(lesson.id);
                  }}>✕</button>
              </div>
            </div>
            {editingId === lesson.id && (
              <LessonEditor
                moduleId={moduleId}
                lesson={lesson}
                onSaved={() => { setEditingId(null); invalidate(); }}
              />
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function LessonEditor({
  moduleId,
  lesson,
  onSaved,
}: {
  moduleId: string;
  lesson: TrainingLesson;
  onSaved: () => void;
}): JSX.Element {
  const [title, setTitle] = useState(lesson.title);
  const [lessonType, setLessonType] = useState(lesson.lesson_type);
  const [minutes, setMinutes] = useState(lesson.estimated_minutes);
  const [content, setContent] = useState<LessonBlock[]>(lesson.content);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/v1/admin/training/modules/${moduleId}/lessons/${lesson.id}`, {
        title,
        lesson_type: lessonType,
        estimated_minutes: minutes,
        content,
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(extractDetail(e)),
  });

  return (
    <div className="p-3 space-y-3 border-t border-border">
      {err && <FormBanner message={err} />}
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Title" required>
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} required />}
        </FormField>
        <FormField label="Type">
          {(id) => (
            <select id={id}
              className="block w-full rounded border border-border bg-surface px-3 py-2 text-ink"
              value={lessonType}
              onChange={(e) => setLessonType(e.target.value as TrainingLesson['lesson_type'])}>
              <option value="reading">Reading</option>
              <option value="exercise">Exercise</option>
              <option value="scenario">Scenario</option>
            </select>
          )}
        </FormField>
        <FormField label="Minutes">
          {(id) => (
            <Input id={id} type="number" min={0} value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value) || 0)} />
          )}
        </FormField>
      </div>

      <div>
        <div className="text-sm font-medium mb-2">Content blocks</div>
        <LessonBlockEditor value={content} onChange={setContent} />
        <ScormPackagesPanel
          moduleId={moduleId}
          lessonId={lesson.id}
          onChanged={() => {
            // The server already patched the lesson's `content` JSON
            // (upload attaches the new scorm block and retires priors;
            // delete detaches the block), so refresh from the server
            // rather than mutating local content state (which would
            // clobber that server-side patch with stale `content` on the
            // next Save).
            onSaved();
          }}
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save lesson'}
        </Button>
      </div>
    </div>
  );
}

// ─── Questions ────────────────────────────────────────────────────────────

function QuestionsSection({
  moduleId,
  questions,
}: {
  moduleId: string;
  questions: QuizQuestionAdmin[];
}): JSX.Element {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<QuestionDraft | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'training', 'module', moduleId] });

  const editing = useMemo(
    () => questions.find((q) => q.id === editingId) ?? null,
    [questions, editingId],
  );
  useEffect(() => {
    if (editing) {
      setDraft({
        question: editing.question,
        explanation: editing.explanation ?? '',
        points: editing.points,
        options: editing.options.map((o) => ({
          option_text: o.option_text,
          is_correct: o.is_correct,
          sort_order: o.sort_order,
        })),
      });
    } else if (!creating) {
      setDraft(null);
    }
  }, [editing, creating]);

  const saveCreate = useMutation({
    mutationFn: (d: QuestionDraft) =>
      api.post(
        `/v1/admin/training/modules/${moduleId}/questions`,
        draftToPayload(d, questions.length),
      ),
    onSuccess: () => { setCreating(false); setDraft(null); invalidate(); },
    onError: (e) => setErr(extractDetail(e)),
  });
  const saveUpdate = useMutation({
    mutationFn: (input: { id: string; d: QuestionDraft; sortOrder: number }) =>
      api.patch(
        `/v1/admin/training/modules/${moduleId}/questions/${input.id}`,
        draftToPayload(input.d, input.sortOrder),
      ),
    onSuccess: () => { setEditingId(null); setDraft(null); invalidate(); },
    onError: (e) => setErr(extractDetail(e)),
  });
  const deleteQ = useMutation({
    mutationFn: (qid: string) =>
      api.delete(`/v1/admin/training/modules/${moduleId}/questions/${qid}`),
    onSuccess: () => { setEditingId(null); setDraft(null); invalidate(); },
    onError: (e) => setErr(extractDetail(e)),
  });
  const reorderQ = useMutation({
    mutationFn: (question_ids: string[]) =>
      api.post(`/v1/admin/training/modules/${moduleId}/questions/reorder`, { question_ids }),
    onSuccess: () => invalidate(),
    onError: (e) => setErr(extractDetail(e)),
  });
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const ids = questions.map((q) => q.id);
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    reorderQ.mutate(ids);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Quiz questions ({questions.length})</CardTitle>
          <Button
            onClick={() => {
              setCreating(true);
              setEditingId(null);
              setDraft(emptyQuestionDraft());
            }}
            disabled={creating}
          >
            + New question
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {err && <FormBanner message={err} />}

        {creating && draft && (
          <QuestionEditor
            value={draft}
            onChange={setDraft}
            saving={saveCreate.isPending}
            onCancel={() => { setCreating(false); setDraft(null); }}
            onSave={() => saveCreate.mutate(draft)}
          />
        )}

        {questions.length === 0 && !creating && (
          <p className="text-sm text-ink-muted">No questions yet.</p>
        )}

        {questions.map((q, idx) => (
          <div key={q.id} className="border border-border rounded">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-surface-alt">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-ink-muted w-6 shrink-0">#{idx + 1}</span>
                <span className="font-medium truncate" title={q.question}>{q.question}</span>
                <Badge tone="neutral">{q.points} pt</Badge>
                <span className="text-xs text-ink-muted">{q.options.length} options</span>
              </div>
              <div className="flex gap-1">
                <Button type="button" variant="secondary"
                  disabled={idx === 0 || reorderQ.isPending}
                  onClick={() => move(idx, -1)}>↑</Button>
                <Button type="button" variant="secondary"
                  disabled={idx === questions.length - 1 || reorderQ.isPending}
                  onClick={() => move(idx, 1)}>↓</Button>
                <Button type="button" variant="secondary"
                  onClick={() => {
                    setCreating(false);
                    setEditingId(editingId === q.id ? null : q.id);
                  }}>
                  {editingId === q.id ? 'Close' : 'Edit'}
                </Button>
              </div>
            </div>
            {editingId === q.id && draft && (
              <div className="p-3 border-t border-border">
                <QuestionEditor
                  value={draft}
                  onChange={setDraft}
                  saving={saveUpdate.isPending}
                  onCancel={() => { setEditingId(null); setDraft(null); }}
                  onDelete={() => {
                    if (confirm('Delete this question?')) deleteQ.mutate(q.id);
                  }}
                  onSave={() => saveUpdate.mutate({ id: q.id, d: draft, sortOrder: q.sort_order })}
                />
              </div>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
